import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface DatePickerProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
    ({ value, onChange, placeholder = 'Pick a date', className, disabled }, ref) => {
        const [isOpen, setIsOpen] = React.useState(false);

        const selectedDate = React.useMemo(() => {
            if (!value) return undefined;
            const [year, month, day] = value.split('-').map(Number);
            return new Date(year, month - 1, day);
        }, [value]);

        const handleSelect = (date: Date | undefined) => {
            if (date && onChange) {
                onChange(format(date, 'yyyy-MM-dd'));
                setIsOpen(false);
            }
        };

        return (
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <Button
                        ref={ref}
                        variant="outline"
                        type="button"
                        disabled={disabled}
                        className={cn(
                            'w-full justify-start text-left font-normal',
                            !value && 'text-muted-foreground',
                            className
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, 'PPP') : <span>{placeholder}</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleSelect}
                    />
                </PopoverContent>
            </Popover>
        );
    }
);
DatePicker.displayName = 'DatePicker';

export { DatePicker };
